/*
  EZOKO - Variant picker auto-selection helper

  Purpose:
  This script improves Shopify product variant picker behavior for products with dependent options.

  Example:
  - Option 1: Model
  - Option 2: Power / Action
  - Option 3: Length

  Problem it solves:
  Some products can land on an invalid option combination. When this happens, the theme may show
  "Unavailable" instead of Add to Cart because the selected options do not resolve to a valid variant.

  Behavior:
  - When Option 1 changes:
    - Keep Option 2 if it is still available with Option 1.
    - Otherwise select the first available Option 2 for Option 1.
    - Then keep Option 3 if it is still available with Option 1 + Option 2.
    - Otherwise select the first available Option 3 for Option 1 + Option 2.

  - When Option 2 changes:
    - Keep Option 1.
    - Keep Option 3 if it is still available with Option 1 + Option 2.
    - Otherwise select the first available Option 3 for Option 1 + Option 2.
    - If Option 1 + Option 2 has no available variant at all, select the first available variant matching Option 2.

  - When Option 3 changes:
    - Keep Option 1 + Option 2 if the full combination is still available.
    - Otherwise try to keep Option 1 and find the first available Option 2 for Option 1 + Option 3.
    - If that fails, select the first available variant matching Option 3.

  Final goal:
  Prevent the picker from remaining on a non-existing or unavailable variant combination.

  Required Liquid support inside the product variant picker:
  - <script data-product-variants type="application/json">{{ product.variants | json }}</script>
  - data-option-position on option controls
  - data-option-value on option controls
*/

(() => {
  if (window.EZOKOVariantAutoSelectLoaded) {
    return;
  }

  window.EZOKOVariantAutoSelectLoaded = true;

  let isAutoSelecting = false;

  /*
    We listen to both click and change because this theme uses hidden radio inputs
    and visible labels. The customer usually clicks the label, not the input.
    We delay the handling by one browser tick so the theme has time to update
    the checked radio state before we read selected options.
  */
  document.addEventListener('click', scheduleOptionChange);
  document.addEventListener('change', scheduleOptionChange);

  function scheduleOptionChange(event) {
    const trigger = event.target.closest('[data-option-position], label[for], button[role="option"][data-option-position]');

    if (!trigger) {
      return;
    }

    window.setTimeout(() => {
      handleOptionChange(event);
    }, 0);
  }

  function handleOptionChange(event) {
    if (isAutoSelecting) {
      return;
    }

    let changedElement = event.target.closest('[data-option-position]');

    if (!changedElement) {
      const label = event.target.closest('label[for]');

      if (label) {
        changedElement = document.getElementById(label.getAttribute('for'));
      }
    }

    if (!changedElement || !changedElement.matches('[data-option-position]')) {
      return;
    }

    const picker = changedElement.closest('variant-picker');

    if (!picker) {
      return;
    }

    const changedPosition = Number(changedElement.dataset.optionPosition);

    /*
      We now allow Option 1, Option 2, and Option 3 to drive correction.
      If the product has only 1 or 2 options, the resolver safely ignores missing options.
    */
    if (![1, 2, 3].includes(changedPosition)) {
      return;
    }

    const variants = getVariants(picker);

    if (!variants) {
      return;
    }

    const selectedOptions = getSelectedOptions(picker);

    if (!selectedOptions[changedPosition - 1]) {
      return;
    }

    const resolvedOptions = resolveBestOptions(variants, selectedOptions, changedPosition);

    if (!resolvedOptions || arraysAreEqual(selectedOptions, resolvedOptions)) {
      return;
    }

    isAutoSelecting = true;

    let lastAutoChangedPosition = null;

    resolvedOptions.forEach((optionValue, index) => {
      const position = index + 1;

      if (!optionValue) {
        return;
      }

      if (selectedOptions[index] === optionValue) {
        return;
      }

      const didSelect = selectOptionValue(picker, position, optionValue);

      if (didSelect) {
        lastAutoChangedPosition = position;
      }
    });

    /*
      Let the theme process our clicks/changes first, then release the lock.
    */
    window.setTimeout(() => {
      isAutoSelecting = false;

      /*
        As a fallback, trigger one final change event from the last auto-selected control.
        This helps themes that only refresh variant state on input change.
      */
      if (lastAutoChangedPosition) {
        const finalChangedInput = getSelectedControlForPosition(picker, lastAutoChangedPosition);

        if (finalChangedInput) {
          finalChangedInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }, 0);
  }

  function getVariants(picker) {
    const variantsScript = picker.querySelector('script[data-product-variants]');

    if (!variantsScript) {
      console.warn('EZOKO variant auto-select: missing script[data-product-variants].');
      return null;
    }

    try {
      const variants = JSON.parse(variantsScript.textContent);

      if (!Array.isArray(variants) || variants.length === 0) {
        console.warn('EZOKO variant auto-select: product variants JSON is empty.');
        return null;
      }

      return variants;
    } catch (error) {
      console.warn('EZOKO variant auto-select: unable to parse variant JSON.', error);
      return null;
    }
  }

function resolveBestOptions(variants, selectedOptions, changedPosition) {
  /*
    Use all existing variants as valid combinations, including sold-out variants.

    Priority rule:
    - If multiple valid variants match, choose an available variant first.
    - If no available variant matches, choose a sold-out variant.
    - Non-existing combinations are still corrected.
  */
  const validVariants = variants.filter((variant) => {
    return Array.isArray(variant.options) && variant.options.length > 0;
  });

  if (validVariants.length === 0) {
    return null;
  }

  const resolved = [...selectedOptions];

  const optionCount = Math.max(
    ...validVariants.map((variant) => Array.isArray(variant.options) ? variant.options.length : 0)
  );

  if (optionCount === 1) {
    const selectedStillValid = validVariants.some((variant) => {
      return variant.options[0] === resolved[0];
    });

    if (!selectedStillValid) {
      resolved[0] = findBestVariant(validVariants, () => true).options[0];
    }

    return resolved;
  }

  if (changedPosition === 1) {
    const option1 = resolved[0];

    const firstVariantForOption1 = findBestVariant(validVariants, (variant) => {
      return variant.options[0] === option1;
    });

    if (!firstVariantForOption1) {
      return null;
    }

    if (optionCount >= 2) {
      const option2StillValid = validVariants.some((variant) => {
        return variant.options[0] === option1 &&
          variant.options[1] === resolved[1];
      });

      if (!option2StillValid) {
        resolved[1] = firstVariantForOption1.options[1];
      }
    }

    if (optionCount >= 3) {
      const option3StillValid = validVariants.some((variant) => {
        return variant.options[0] === resolved[0] &&
          variant.options[1] === resolved[1] &&
          variant.options[2] === resolved[2];
      });

      if (!option3StillValid) {
        const firstVariantForOption1And2 = findBestVariant(validVariants, (variant) => {
          return variant.options[0] === resolved[0] &&
            variant.options[1] === resolved[1];
        });

        if (!firstVariantForOption1And2) {
          return null;
        }

        resolved[2] = firstVariantForOption1And2.options[2];
      }
    }

    return resolved;
  }

  if (changedPosition === 2) {
    const option2 = resolved[1];

    const option1And2StillValid = validVariants.some((variant) => {
      return variant.options[0] === resolved[0] &&
        variant.options[1] === option2;
    });

    if (!option1And2StillValid) {
      const firstVariantForOption2 = findBestVariant(validVariants, (variant) => {
        return variant.options[1] === option2;
      });

      if (!firstVariantForOption2) {
        return null;
      }

      resolved[0] = firstVariantForOption2.options[0];
    }

    if (optionCount >= 3) {
      const option3StillValid = validVariants.some((variant) => {
        return variant.options[0] === resolved[0] &&
          variant.options[1] === resolved[1] &&
          variant.options[2] === resolved[2];
      });

      if (!option3StillValid) {
        const firstVariantForOption1And2 = findBestVariant(validVariants, (variant) => {
          return variant.options[0] === resolved[0] &&
            variant.options[1] === resolved[1];
        });

        if (!firstVariantForOption1And2) {
          return null;
        }

        resolved[2] = firstVariantForOption1And2.options[2];
      }
    }

    return resolved;
  }

  if (changedPosition === 3) {
    const option3 = resolved[2];

    const fullCombinationStillValid = validVariants.some((variant) => {
      return variant.options[0] === resolved[0] &&
        variant.options[1] === resolved[1] &&
        variant.options[2] === option3;
    });

    if (fullCombinationStillValid) {
      return resolved;
    }

    const firstVariantForOption1And3 = findBestVariant(validVariants, (variant) => {
      return variant.options[0] === resolved[0] &&
        variant.options[2] === option3;
    });

    if (firstVariantForOption1And3) {
      resolved[1] = firstVariantForOption1And3.options[1];
      return resolved;
    }

    const firstVariantForOption3 = findBestVariant(validVariants, (variant) => {
      return variant.options[2] === option3;
    });

    if (!firstVariantForOption3) {
      return null;
    }

    resolved[0] = firstVariantForOption3.options[0];
    resolved[1] = firstVariantForOption3.options[1];

    return resolved;
  }

  return resolved;
}

function findBestVariant(variants, predicate) {

  /*

    Return an available matching variant first.

    If none is available, return the first matching sold-out variant.

  */

  const availableMatch = variants.find((variant) => {

    return variant.available && predicate(variant);

  });

  if (availableMatch) {

    return availableMatch;

  }

  return variants.find(predicate) || null;

}

  function getSelectedOptions(picker) {
    const selectedOptions = [];

    /*
      Prefer checked radios first.
    */
    const radioInputs = picker.querySelectorAll('input[type="radio"][data-option-position]');

    radioInputs.forEach((input) => {
      if (!input.checked) {
        return;
      }

      const position = Number(input.dataset.optionPosition);

      if (!position) {
        return;
      }

      selectedOptions[position - 1] = input.dataset.optionValue || input.value;
    });

    /*
      Then fill missing options from hidden inputs, mainly for dropdown selectors.
    */
    const hiddenInputs = picker.querySelectorAll('input[type="hidden"][data-option-position]');

    hiddenInputs.forEach((input) => {
      const position = Number(input.dataset.optionPosition);

      if (!position) {
        return;
      }

      if (!selectedOptions[position - 1]) {
        selectedOptions[position - 1] = input.dataset.optionValue || input.value;
      }
    });

    return selectedOptions;
  }

  function selectOptionValue(picker, position, optionValue) {
    const radio = Array.from(
      picker.querySelectorAll(`input[type="radio"][data-option-position="${position}"]`)
    ).find((input) => input.dataset.optionValue === optionValue);

    if (radio) {
      const label = radio.id ? picker.querySelector(`label[for="${cssEscape(radio.id)}"]`) : null;

      if (label) {
        label.click();
      } else {
        radio.click();
      }

      radio.checked = true;
      updateVisibleOptionValue(picker, position, optionValue);

      return true;
    }

    const dropdownButton = Array.from(
      picker.querySelectorAll(`button[role="option"][data-option-position="${position}"]`)
    ).find((button) => button.dataset.optionValue === optionValue);

    if (dropdownButton) {
      dropdownButton.click();
      updateDropdownSelectedState(picker, position, dropdownButton);
      updateDropdownHiddenInput(picker, position, dropdownButton);
      updateVisibleOptionValue(picker, position, optionValue);

      return true;
    }

    const hiddenInput = picker.querySelector(`input[type="hidden"][data-option-position="${position}"]`);

    if (hiddenInput) {
      hiddenInput.dataset.optionValue = optionValue;
      hiddenInput.value = optionValue;
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      updateVisibleOptionValue(picker, position, optionValue);

      return true;
    }

    return false;
  }

  function getSelectedControlForPosition(picker, position) {
    const checkedRadio = picker.querySelector(`input[type="radio"][data-option-position="${position}"]:checked`);

    if (checkedRadio) {
      return checkedRadio;
    }

    return picker.querySelector(`input[type="hidden"][data-option-position="${position}"]`);
  }

  function updateVisibleOptionValue(picker, position, optionValue) {
    const optionSelectors = picker.querySelectorAll('.product-form__option-selector');

    optionSelectors.forEach((selector) => {
      const control = selector.querySelector(`[data-option-position="${position}"]`);

      if (!control) {
        return;
      }

      const visibleValue = selector.querySelector('.product-form__option-value, .select__selected-value');

      if (visibleValue) {
        visibleValue.textContent = optionValue;
      }
    });
  }

  function updateDropdownSelectedState(picker, position, selectedButton) {
    const buttons = picker.querySelectorAll(`button[role="option"][data-option-position="${position}"]`);

    buttons.forEach((button) => {
      button.setAttribute('aria-selected', button === selectedButton ? 'true' : 'false');
    });
  }

  function updateDropdownHiddenInput(picker, position, selectedButton) {
    const hiddenInput = picker.querySelector(`input[type="hidden"][data-option-position="${position}"]`);

    if (!hiddenInput) {
      return;
    }

    hiddenInput.value = selectedButton.value;
    hiddenInput.dataset.optionValue = selectedButton.dataset.optionValue || selectedButton.value;
  }

  function arraysAreEqual(firstArray, secondArray) {
    if (!firstArray || !secondArray) {
      return false;
    }

    if (firstArray.length !== secondArray.length) {
      return false;
    }

    return firstArray.every((value, index) => value === secondArray[index]);
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/"/g, '\\"');
  }
})();
/*
  EZOKO – Product media bridge (v3.3)
 
  Delta vs v3.2:
    - Adds `allow-partial-rerender` to every <product-rerender> element
      on init. Without it, Focal does a full-DOM replace on every
      variant:change, which destroys <product-media> and rebuilds the
      carousel at the new variant's canonical featured_media — which the
      user perceives as the carousel "reverting".
    - With the attribute set, Focal does partial replacement: it updates
      only the picker / price / inventory / buy-buttons / etc blocks and
      leaves <product-media> (and our Flickity instance) untouched.
      Combined with the existing mediaRoot.selectedVariantMediaId
      pre-set, the swiped slide stays visible after the sync.
 
  Everything else (size-preserving sync, blank-wrapper hardening,
  overlay/sibling/product-level fallback, re-binding on product:rerender,
  250ms cascade lockout) is unchanged from v3.2.
 
  Replace ONLY the Product media bridge IIFE in custom.js. The Variant
  auto-select helper IIFE above this one must stay as-is.
*/
 
(() => {
  if (window.EZOKOProductMediaBridgeLoaded) {
    return;
  }
  window.EZOKOProductMediaBridgeLoaded = true;
 
  const DEBUG = window.EZOKO_DEBUG !== false;
  const log = (...a) => { if (DEBUG) console.log('[EZOKO]', ...a); };
  const warn = (...a) => { if (DEBUG) console.warn('[EZOKO]', ...a); };
 
  let isSyncingFromMedia = false;
 
  // ----- bootstrap --------------------------------------------------
 
  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('product:rerender', () => setTimeout(initAll, 0));
  if (document.readyState !== 'loading') initAll();
 
  function initAll() {
    // KEY FIX (v3.3): force partial-rerender so the carousel survives variant changes.
    document.querySelectorAll('product-rerender').forEach((el) => {
      if (!el.hasAttribute('allow-partial-rerender')) {
        el.setAttribute('allow-partial-rerender', '');
        log('init — added allow-partial-rerender to <product-rerender>', el.id || '(no id)');
      }
    });
 
    const bridge = window.EZOKO_PRODUCT_MEDIA_BRIDGE || {};
 
    Object.keys(bridge).forEach((productId) => {
      const config = bridge[productId];
      if (!config || !config.formId) return;
 
      const mediaRoot = document.querySelector(
        `product-media[form-id="${cssEscape(config.formId)}"]`
      );
      if (!mediaRoot) return;
      if (mediaRoot.dataset.ezokoMediaBridgeBound === 'true') return;
 
      mediaRoot.dataset.ezokoMediaBridgeBound = 'true';
      log('init — binding product', productId, 'form', config.formId);
 
      wireBlankWrapperFix(mediaRoot);
      wireSwipeSync(mediaRoot, config);
 
      const picker = document.querySelector(
        `variant-picker[form-id="${cssEscape(config.formId)}"]`
      );
      const currentVariant = picker ? getCurrentVariantFromPicker(picker) : null;
      if (currentVariant && currentVariant.id) {
        const info = config.variants && config.variants[String(currentVariant.id)];
        if (info && info.featuredMediaId) {
          mediaRoot.selectedVariantMediaId = info.featuredMediaId;
        }
        applyVariantOverride(config, mediaRoot, currentVariant.id);
      }
    });
  }
 
  // ----- variant change → overlay/fallback -------------------------
 
  document.addEventListener('variant:change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;
 
    const form = event.target && event.target.closest ? event.target.closest('form') : null;
    const formId = form ? form.id : null;
 
    setTimeout(() => {
      const config = findBridgeConfig(variant.id, formId);
      if (!config) return;
      const mediaRoot = document.querySelector(
        `product-media[form-id="${cssEscape(config.formId)}"]`
      );
      if (!mediaRoot) return;
      applyVariantOverride(config, mediaRoot, variant.id);
    }, 0);
  });
 
  async function applyVariantOverride(config, mediaRoot, variantId) {
    const variantInfo = config.variants && config.variants[String(variantId)];
    if (!variantInfo) return;
 
    const overrideContainer = mediaRoot.querySelector('[data-ezoko-media-override]');
    const overrideWrapper = mediaRoot.querySelector('[data-ezoko-media-override-wrapper]');
    const overrideImage = mediaRoot.querySelector('[data-ezoko-media-override-image]');
    if (!overrideContainer || !overrideWrapper || !overrideImage) return;
 
    if (variantInfo.featuredMediaId) {
      hideOverlay(overrideContainer, overrideImage);
      return;
    }
    if (variantInfo.siblingMediaId) {
      hideOverlay(overrideContainer, overrideImage);
      await selectMediaInCarousel(mediaRoot, variantInfo.siblingMediaId);
      return;
    }
    if (Array.isArray(config.productLevelMediaIds) && config.productLevelMediaIds.length > 0) {
      hideOverlay(overrideContainer, overrideImage);
      await selectMediaInCarousel(mediaRoot, config.productLevelMediaIds[0]);
      return;
    }
    showOverlay(overrideContainer, overrideWrapper, overrideImage, config.comingSoon);
  }
 
  async function selectMediaInCarousel(mediaRoot, mediaId) {
    const carousel = mediaRoot.querySelector('flickity-carousel');
    if (!carousel || typeof carousel.select !== 'function') return;
    try { await carousel.select(`[data-media-id="${mediaId}"]`); } catch (e) {}
  }
 
  function hideOverlay(c, img) {
    c.hidden = true;
    c.setAttribute('aria-hidden', 'true');
    img.removeAttribute('src');
  }
 
  function showOverlay(c, w, img, comingSoon) {
    if (!comingSoon || !comingSoon.src) { hideOverlay(c, img); return; }
    const ar = comingSoon.aspectRatio
      || (comingSoon.width && comingSoon.height ? comingSoon.width / comingSoon.height : 1);
    img.src = comingSoon.src;
    img.width = comingSoon.width || 1200;
    img.height = comingSoon.height || Math.round((comingSoon.width || 1200) / ar);
    if (!img.getAttribute('alt')) img.setAttribute('alt', 'Coming soon');
    w.style.setProperty('--aspect-ratio', ar);
    w.style.paddingBottom = `${100 / ar}%`;
    c.hidden = false;
    c.setAttribute('aria-hidden', 'false');
  }
 
  // ----- blank-wrapper hardening -----------------------------------
 
  function wireBlankWrapperFix(mediaRoot) {
    clearRevealOnSelected(mediaRoot);
    const carousel = mediaRoot.querySelector('flickity-carousel');
    if (!carousel) return;
    carousel.addEventListener('flickity:ready', () => clearRevealOnSelected(mediaRoot));
    carousel.addEventListener('flickity:slide-changed', () => clearRevealOnSelected(mediaRoot));
    carousel.addEventListener('flickity:slide-settled', async () => {
      clearRevealOnSelected(mediaRoot);
      if (carousel.flickityInstance) {
        const f = await carousel.flickityInstance;
        clearReveal(f && f.selectedElement ? f.selectedElement.querySelector('img') : null);
      }
    });
  }
 
  function clearRevealOnSelected(mediaRoot) {
    const sel = mediaRoot.querySelector(
      '.product__media-item.is-selected, .product__media-item.is-initial-selected'
    );
    if (sel) clearReveal(sel.querySelector('img'));
  }
 
  function clearReveal(image) {
    if (!image) return;
    image.removeAttribute('reveal');
    image.style.opacity = '';
    image.style.transform = '';
    image.classList.remove('is-revealing');
    if (image.getAttribute('loading') === 'lazy') image.setAttribute('loading', 'eager');
    if (!image.hasAttribute('fetchpriority')) image.setAttribute('fetchpriority', 'high');
  }
 
  // ----- swipe → picker sync (size-preserving) ---------------------
 
  function wireSwipeSync(mediaRoot, config) {
    if (!config.colorOptionPosition) {
      warn('sync/wire — colorOptionPosition is 0, swipe sync disabled for product', config.productId);
      return;
    }
    const carousel = mediaRoot.querySelector('flickity-carousel');
    if (!carousel) return;
    carousel.addEventListener('flickity:slide-settled', () => {
      syncFromSelectedMedia(mediaRoot, config);
    });
    log('sync/wire — bound flickity:slide-settled for product', config.productId);
  }
 
  async function syncFromSelectedMedia(mediaRoot, config) {
    if (isSyncingFromMedia) { log('sync/skip — already syncing'); return; }
 
    const carousel = mediaRoot.querySelector('flickity-carousel');
    if (!carousel) return;
 
    let settledSlide = null;
    if (carousel.flickityInstance) {
      const f = await carousel.flickityInstance;
      settledSlide = f && f.selectedElement;
    }
    if (!settledSlide) settledSlide = mediaRoot.querySelector('.product__media-item.is-selected');
    if (!settledSlide) { log('sync/02 — no settled slide'); return; }
 
    const mediaId = settledSlide.getAttribute('data-media-id');
    if (!mediaId) { log('sync/03 — no data-media-id'); return; }
 
    const mapping = config.mediaToVariant && config.mediaToVariant[String(mediaId)];
    if (!mapping || !mapping.colorValue) {
      log('sync/04 — media', mediaId, 'has no variant/color mapping (product-level slide, ignored)');
      return;
    }
 
    const picker = document.querySelector(
      `variant-picker[form-id="${cssEscape(config.formId)}"]`
    );
    if (!picker || typeof picker.selectCombination !== 'function') {
      warn('sync/05 — picker not ready');
      return;
    }
 
    const colorIdx = config.colorOptionPosition - 1;
    if (colorIdx < 0) return;
 
    const currentVariant = getCurrentVariantFromPicker(picker);
    const currentOptions = (currentVariant && Array.isArray(currentVariant.options))
      ? currentVariant.options.slice()
      : [];
 
    if (currentOptions.length === 0) {
      warn('sync/06 — picker has no current variant');
      return;
    }
 
    if (norm(currentOptions[colorIdx]) === norm(mapping.colorValue)) {
      log('sync/07 — color already matches, no-op');
      return;
    }
 
    const idealOptions = currentOptions.slice();
    idealOptions[colorIdx] = mapping.colorValue;
 
    let resolvedVariant = findVariantByOptions(config, idealOptions);
 
    if (!resolvedVariant) {
      resolvedVariant = config.variants && config.variants[String(mapping.variantId)];
      log('sync — no (current size, new color) variant exists; falling back to swiped variant');
    } else {
      log('sync — preserving size:', currentOptions[1 - colorIdx], 'switching color to', mapping.colorValue);
    }
 
    if (!resolvedVariant || !Array.isArray(resolvedVariant.options)) {
      warn('sync/08 — could not resolve target variant');
      return;
    }
 
    if (currentVariant && String(currentVariant.id) === String(resolvedVariant.id)) {
      log('sync/09 — already on target variant', resolvedVariant.id);
      return;
    }
 
    isSyncingFromMedia = true;
 
    if (resolvedVariant.featuredMediaId) {
      mediaRoot.selectedVariantMediaId = resolvedVariant.featuredMediaId;
    }
 
    resolvedVariant.options.forEach((value, index) => {
      const position = index + 1;
      const radios = picker.querySelectorAll(
        `input[type="radio"][data-option-position="${position}"]`
      );
      radios.forEach((radio) => {
        const matches = norm(radio.dataset.optionValue || radio.value) === norm(value);
        if (matches !== radio.checked) radio.checked = matches;
      });
      const hidden = picker.querySelector(
        `input[type="hidden"][data-option-position="${position}"]`
      );
      if (hidden) {
        hidden.value = value;
        if (hidden.dataset) hidden.dataset.optionValue = value;
      }
      updateVisibleOptionValue(picker, position, value);
    });
 
    // v3.4: Focal's selectCombination expects option-value IDs (input.value),
// not the human strings stored in variant.options. Translate.
const optionValueIds = resolvedVariant.options.map((optionString, index) => {
  const position = index + 1;
  const radioMatch = Array.from(
    picker.querySelectorAll(`input[data-option-position="${position}"]`)
  ).find(r => norm(r.dataset.optionValue || '') === norm(optionString));
  if (radioMatch) return radioMatch.value;
  const buttonMatch = Array.from(
    picker.querySelectorAll(`button[data-option-position="${position}"]`)
  ).find(b => norm(b.dataset.optionValue || '') === norm(optionString));
  if (buttonMatch) return buttonMatch.value;
  return optionString;
});

try {
  log('sync — calling selectCombination with IDs', optionValueIds, 'from names', resolvedVariant.options);
  await picker.selectCombination({
    optionValues: optionValueIds,
    productChange: false
  });
  log('sync — selectCombination resolved');
}
     catch (error) {
      warn('sync/10 — selectCombination threw', error);
      const firstRadio = picker.querySelector(
        `input[type="radio"][data-option-position="1"]:checked`
      );
      if (firstRadio) firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      setTimeout(() => { isSyncingFromMedia = false; }, 250);
    }
  }
 
  function findVariantByOptions(config, targetOptions) {
    if (!config.variants) return null;
    const target = targetOptions.map(norm);
    return Object.values(config.variants).find((v) => {
      if (!Array.isArray(v.options)) return false;
      if (v.options.length !== target.length) return false;
      return v.options.every((opt, i) => norm(opt) === target[i]);
    });
  }
 
  // ----- picker helpers --------------------------------------------
 
  function getCurrentVariantFromPicker(picker) {
    const script = picker.querySelector('script[data-variant]');
    if (!script || !script.textContent.trim()) return null;
    try { return JSON.parse(script.textContent); } catch (e) { return null; }
  }
 
  function updateVisibleOptionValue(picker, position, optionValue) {
    const control = picker.querySelector(`[data-option-position="${position}"]`);
    const wrapper = control && control.closest('.product-form__option-selector');
    if (!wrapper) return;
    const valueElement = wrapper.querySelector(
      '.product-form__option-value, .select__selected-value'
    );
    if (valueElement) valueElement.textContent = optionValue;
  }
 
  function findBridgeConfig(variantId, formId) {
    const bridge = window.EZOKO_PRODUCT_MEDIA_BRIDGE || {};
    return Object.values(bridge).find((config) => {
      if (formId && config.formId !== formId) return false;
      return config.variants && config.variants[String(variantId)];
    });
  }
 
  // ----- utilities -------------------------------------------------
 
  function norm(value) {
    return String(value || '').trim().toLowerCase();
  }
 
  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }
    return String(value).replace(/"/g, '\\"');
  }
})();